import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type {
  Question,
  AnswerOption,
  QuestionnaireState,
  QuestionnaireAnswer,
  StatusResult
} from '../types';
import { API_BASE_URL } from '../config';

interface QuestionnaireContextType {
  state: QuestionnaireState;
  questions: Question[];
  options: Record<string, AnswerOption[]>;
  currentQuestion: Question | null;
  currentOptions: AnswerOption[];
  progress: number;
  isLoading: boolean;
  answerQuestion: (answerId: string, value?: string | number | Date) => void;
  goBack: () => void;
  reset: () => void;
  getResult: () => Promise<StatusResult | null>;
}

type Action =
  | { type: 'SET_QUESTIONS'; payload: { questions: Question[]; options: Record<string, AnswerOption[]> } }
  | { type: 'ANSWER_QUESTION'; payload: { questionId: string; answerId: string; value?: string | number | Date; nextQuestionId?: string } }
  | { type: 'GO_BACK' }
  | { type: 'RESET' }
  | { type: 'COMPLETE' }
  | { type: 'SET_LOADING'; payload: boolean };

interface State {
  questionnaire: QuestionnaireState;
  questions: Question[];
  options: Record<string, AnswerOption[]>;
  isLoading: boolean;
}

const initialQuestionnaireState: QuestionnaireState = {
  currentQuestionId: null,
  answers: [],
  history: [],
  isComplete: false,
  startedAt: new Date(),
};

const initialState: State = {
  questionnaire: initialQuestionnaireState,
  questions: [],
  options: {},
  isLoading: true,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_QUESTIONS': {
      const firstQuestion = action.payload.questions.find(q => q.orderIndex === 1);
      return {
        ...state,
        questions: action.payload.questions,
        options: action.payload.options,
        questionnaire: {
          ...state.questionnaire,
          currentQuestionId: firstQuestion?.id || null,
        },
        isLoading: false,
      };
    }

    case 'ANSWER_QUESTION': {
      const { questionId, answerId, value, nextQuestionId } = action.payload;
      const newAnswer: QuestionnaireAnswer = { questionId, answerId, value };

      // Remove any existing answer for this question
      const filteredAnswers = state.questionnaire.answers.filter(a => a.questionId !== questionId);

      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          answers: [...filteredAnswers, newAnswer],
          history: [...state.questionnaire.history, questionId],
          currentQuestionId: nextQuestionId || null,
          isComplete: !nextQuestionId,
          completedAt: !nextQuestionId ? new Date() : undefined,
        },
      };
    }

    case 'GO_BACK': {
      if (state.questionnaire.history.length === 0) return state;

      const newHistory = [...state.questionnaire.history];
      const previousQuestionId = newHistory.pop();

      // Remove the answer for the current question
      const currentQuestionId = state.questionnaire.currentQuestionId;
      const newAnswers = state.questionnaire.answers.filter(
        a => a.questionId !== currentQuestionId
      );

      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          currentQuestionId: previousQuestionId || null,
          history: newHistory,
          answers: newAnswers,
          isComplete: false,
          completedAt: undefined,
        },
      };
    }

    case 'RESET': {
      const firstQuestion = state.questions.find(q => q.orderIndex === 1);
      return {
        ...state,
        questionnaire: {
          ...initialQuestionnaireState,
          currentQuestionId: firstQuestion?.id || null,
          startedAt: new Date(),
        },
      };
    }

    case 'COMPLETE': {
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          isComplete: true,
          completedAt: new Date(),
        },
      };
    }

    case 'SET_LOADING': {
      return { ...state, isLoading: action.payload };
    }

    default:
      return state;
  }
}

const QuestionnaireContext = createContext<QuestionnaireContextType | null>(null);

export function QuestionnaireProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Fetch questions on mount
  useEffect(() => {
    async function fetchQuestions() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/questions`);
        const data = await response.json();
        if (data.success) {
          dispatch({
            type: 'SET_QUESTIONS',
            payload: { questions: data.data.questions, options: data.data.options }
          });
        }
      } catch (error) {
        console.error('Failed to fetch questions:', error);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
    fetchQuestions();
  }, []);

  const currentQuestion = state.questionnaire.currentQuestionId
    ? state.questions.find(q => q.id === state.questionnaire.currentQuestionId) || null
    : null;

  const currentOptions = currentQuestion
    ? state.options[currentQuestion.id] || []
    : [];

  const progress = state.questionnaire.history.length / Math.max(state.questions.length, 1) * 100;

  const answerQuestion = useCallback((answerId: string, value?: string | number | Date) => {
    if (!currentQuestion) return;

    const selectedOption = currentOptions.find(o => o.id === answerId);
    const nextQuestionId = selectedOption?.leadsToQuestionId;

    dispatch({
      type: 'ANSWER_QUESTION',
      payload: {
        questionId: currentQuestion.id,
        answerId,
        value,
        nextQuestionId,
      },
    });
  }, [currentQuestion, currentOptions]);

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const getResult = useCallback(async (): Promise<StatusResult | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: state.questionnaire.answers }),
      });
      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Failed to get result:', error);
      return null;
    }
  }, [state.questionnaire.answers]);

  return (
    <QuestionnaireContext.Provider
      value={{
        state: state.questionnaire,
        questions: state.questions,
        options: state.options,
        currentQuestion,
        currentOptions,
        progress,
        isLoading: state.isLoading,
        answerQuestion,
        goBack,
        reset,
        getResult,
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  );
}

export function useQuestionnaire() {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
}
